import React, {useEffect, useState} from 'react';
import {Button, Form, Input, Select, Modal, Divider, Row, Col, message} from 'antd';
import {CommandDto, CommandDtoExecuteOptionEnum, ExecuteOptionModifier, LocateDto, PasswordControllerApi} from '../../../api';
import {EditOutlined, FunctionOutlined, PlayCircleOutlined} from "@ant-design/icons";
import DescriptionFormItem from "../../form/DescriptionFormItem";
import ExpressionBuilderModal from "../ExpressionBuilderModal";
import LocatorList from "./locator/LocatorList";
import {listToLocateDto, locateDtoToList} from "../../../utils/LocatorUtils";
import ExecuteCommandOptionComponent from "./component/ExecuteCommandOptionComponent";

const tailLayout = {
    wrapperCol: {offset: 8, span: 16},
};

const ExecuteCommandModal = ({onCommandCardData, initialData}: { onCommandCardData: Function; initialData?: CommandDto }) => {
    const {Option} = Select;
    const [form] = Form.useForm();
    const [showCheckValue, setShowCheckValue] = useState(false);
    const [executeOption, setExecuteOption] = useState();
    const [isModalVisible, setIsModalVisible] = useState(false); // State für die Modal-Sichtbarkeit
    const [isExpressionBuilderVisible, setIsExpressionBuilderVisible] = useState(false);
    const [executeValue, setExecuteValue] = useState<string>();
    const [passwordText, setPasswordText] = useState<string>();

    // Die Locators
    const [locatorData, setLocatorData] = useState<LocateDto[]>([]);

    const passwordControllerApi = new PasswordControllerApi();

    useEffect(() => {
        // Falls der ExecuteValue eine spezielle Funktion ist und dort etwas aufgelöst werden soll
        if (initialData?.executeValue) resolveExecuteValueAndSetFieldValues(initialData.executeValue);
        if (initialData?.executeOption) {
            form.setFieldValue('executeOption', initialData.executeOption);
            onExecuteOptionChange(initialData.executeOption);
        }
        if (initialData?.locateDto) setLocatorData(locateDtoToList(initialData.locateDto));
        if (initialData?.description) form.setFieldValue('description', initialData.description);
        setInitialOptionModifierDataInForm(initialData);

    }, [initialData]);

    const setInitialOptionModifierDataInForm = (initialData) => {
        if (initialData && initialData.executeOptionModifier) {
            const executeOptionModifier: ExecuteOptionModifier = initialData.executeOptionModifier;

            form.setFieldsValue({
                force: executeOptionModifier.force,
                timeout: executeOptionModifier.timeout,
                position: executeOptionModifier.position ? {x: executeOptionModifier.position.x, y: executeOptionModifier.position.y} : undefined,
                sourcePosition: executeOptionModifier.sourcePosition ? {x: executeOptionModifier.sourcePosition.x, y: executeOptionModifier.sourcePosition.y} : undefined,
                targetPosition: executeOptionModifier.targetPosition ? {x: executeOptionModifier.targetPosition.x, y: executeOptionModifier.targetPosition.y} : undefined,
                trial: executeOptionModifier.trial,
                mouseButton: executeOptionModifier.button,
                clickCount: executeOptionModifier.clickCount,
                delay: executeOptionModifier.delay
            });
        }
    }

    const onFinish = (values) => {
        const hasValues =
            values.force ||
            values.timeout ||
            values.trial ||
            (values.position && (values.position.x || values.position.y)) ||
            values.button ||
            values.clickCount ||
            values.delay ||
            (values.sourcePosition && (values.sourcePosition.x || values.sourcePosition.y)) ||
            (values.targetPosition && (values.targetPosition.x || values.targetPosition.y));

        let modifier: ExecuteOptionModifier | undefined = undefined;

        // Nur ein modifier erstellen, wenn mindestens ein Wert ausgefüllt ist
        if (hasValues) {
            modifier = {
                force: values.force,
                timeout: values.timeout,
                trial: values.trial,
                position: values.position || undefined, // optional, falls nicht vorhanden
                button: values.mouseButton,
                clickCount: values.clickCount,
                delay: values.delay,
                sourcePosition: values.sourcePosition || undefined, // optional, falls nicht vorhanden
                targetPosition: values.targetPosition || undefined // optional, falls nicht vorhanden
            };
        }


        let locate: LocateDto = listToLocateDto(locatorData);


        let command: CommandDto = {
            locateDto: locate,
            commandType: 'EXECUTE',
            executeOption: values.executeOption,
            executeValue: executeValue ? executeValue : values.executeValue,
            passwordDto: passwordText ? {name: passwordText} : undefined,
            description: values.description,
            executeOptionModifier: modifier ? modifier : undefined
        };
        onCommandCardData(command);
        setIsModalVisible(false); // Modal schließen
    };

    const onReset = () => {
        form.resetFields();
    };

    const allExecuteOptions = Object.values(CommandDtoExecuteOptionEnum);

    const allowedOptions = [CommandDtoExecuteOptionEnum.Press, CommandDtoExecuteOptionEnum.Fill,
        CommandDtoExecuteOptionEnum.PressSequentially, CommandDtoExecuteOptionEnum.SelectOption];

    const onExecuteOptionChange = (value) => {
        setShowCheckValue(allowedOptions.includes(value));
        setExecuteOption(value);
    };

    // Funktion zum Öffnen des Modals
    const showModal = () => {
        setIsModalVisible(true);
    };

    // Funktion zum Schließen des Modals
    const handleCancel = () => {
        setIsModalVisible(false);
    };


    const handleOpenExpressionBuilder = () => {
        setIsExpressionBuilderVisible(true);
    };

    const handleExpressionBuilderCancel = () => {
        setIsExpressionBuilderVisible(false);
    };

    const resolveExecuteValueAndSetFieldValues = (expression: string) => {
        setExecuteValue(expression);
        if (expression.includes('password')) {
            const regex = /\((\d+)\)/;
            const match = expression.match(regex);
            if (match) {
                let passwordId = parseInt(match[1], 10);
                passwordControllerApi.getPasswordNameAndDescriptionById(passwordId)
                    .then((result) => {
                        if (result.data && result.data.name) {
                            form.setFieldValue('executeValue', "Passwort: " + result.data.name);
                            setPasswordText(result.data.name);
                        }
                    }).catch((error) => {
                        message.error("Fehler bei der Auflösung der ID des Passworts!");
                    }
                )
            }
        } else {
            form.setFieldValue('executeValue', expression)
        }
    }


    return (
        <>
            {initialData == null &&
                <div style={{display: 'flex', alignItems: 'stretch', justifyContent: 'stretch'}}>
                  <Button
                      type="primary"
                      onClick={showModal}
                      style={{width: '100%', height: '100%'}}
                  >
                    <PlayCircleOutlined/>Ausführen
                  </Button>
                </div>}
            {initialData != null &&
                <Button
                    type="primary"
                    style={{float: 'right'}}
                    onClick={showModal}
                >
                  <EditOutlined/>
                </Button>
            }
            <Modal
                title="Ausführung"
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    name="control-hooks"
                    onFinish={onFinish}
                    layout="vertical"
                >
                    <LocatorList locateDtoList={locatorData} onChange={setLocatorData}/>
                    <Divider/>
                    <Form.Item name="executeOption" label="Ausführungsoption" rules={[{required: true, message: 'Bitte wähle eine Ausführungsoption aus'}]}>
                        <Select
                            placeholder="Select an Execute-Option"
                            onChange={onExecuteOptionChange}
                            allowClear
                        >
                            {allExecuteOptions.map((option) => (
                                <Option key={option} value={option}>
                                    {option}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {showCheckValue && (
                        <Form.Item name="executeValue" label="Ausführungswert" rules={[{required: true, message: 'Bitte gebe einen Ausführungswert ein'}]}>
                            <Input onChange={(e) => setExecuteValue(e.target.value)} addonAfter={<Button onClick={handleOpenExpressionBuilder}><FunctionOutlined/></Button>}/>
                        </Form.Item>
                    )}
                    {executeOption && (
                        <ExecuteCommandOptionComponent selectedOption={executeOption} form={form}/>
                    )}
                    <DescriptionFormItem/>
                    <Form.Item>
                        <Row justify="center">
                            <Col style={{marginRight: 5}}>
                                <Button type="primary" htmlType="submit">
                                    {initialData ? "Speichern" : "Hinzufügen"}
                                </Button>
                            </Col>
                            <Col>
                                <Button htmlType="button" onClick={onReset}>
                                    Zurücksetzen
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
                <ExpressionBuilderModal
                    isVisible={isExpressionBuilderVisible}
                    onSave={resolveExecuteValueAndSetFieldValues}
                    handleCancel={handleExpressionBuilderCancel}
                />
            </Modal>
        </>
    );
};

export default ExecuteCommandModal;
